"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useState } from "react";
import {
  CalendarDays,
  FileText,
  Settings,
  Users,
  BarChart3,
  Zap,
  ChevronRight,
  ChevronDown,
  Eye,
  Layers,
  LayoutDashboard,
  ClipboardCheck,
  Building2,
  ChevronsUpDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ClientProvider, useClient } from "@/lib/client-context";
import { mockClients } from "@/lib/mock-data";

const contentSubNav = [
  { href: "/content/calendar", label: "Calendar", icon: CalendarDays },
  { href: "/content/posts", label: "Posts", icon: FileText },
  { href: "/content/feed", label: "Feed View", icon: Eye },
];

const bottomNav = [
  { href: "/team", label: "Team", icon: Users },
  { href: "/settings", label: "Settings", icon: Settings },
];

function ClientSelector() {
  const { selectedClientId, setSelectedClientId } = useClient();
  const [open, setOpen] = useState(false);

  const selected =
    selectedClientId === "all"
      ? null
      : mockClients.find((c) => c.id === selectedClientId);

  return (
    <div className="px-4 pb-3">
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 bg-sidebar-accent/60 hover:bg-sidebar-accent transition-colors text-left"
        >
          {selected ? (
            <div
              className={cn(
                "h-7 w-7 rounded-md flex items-center justify-center text-white text-[10px] font-bold shrink-0",
                selected.color
              )}
            >
              {selected.initials}
            </div>
          ) : (
            <div className="h-7 w-7 rounded-md flex items-center justify-center bg-sidebar-foreground/10 shrink-0">
              <Building2 className="h-3.5 w-3.5 text-sidebar-foreground/60" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-sidebar-foreground/50 leading-tight">
              Client
            </p>
            <p className="text-xs font-medium text-sidebar-foreground truncate leading-tight">
              {selected ? selected.name : "All Clients"}
            </p>
          </div>
          <ChevronsUpDown className="h-3.5 w-3.5 text-sidebar-foreground/40 shrink-0" />
        </button>

        {open && (
          <div className="absolute top-full left-0 right-0 mt-1 rounded-lg bg-sidebar border border-sidebar-border shadow-xl z-50 overflow-hidden">
            <button
              onClick={() => {
                setSelectedClientId("all");
                setOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-sidebar-accent transition-colors",
                selectedClientId === "all" && "bg-sidebar-accent"
              )}
            >
              <div className="h-7 w-7 rounded-md flex items-center justify-center bg-sidebar-foreground/10 shrink-0">
                <Building2 className="h-3.5 w-3.5 text-sidebar-foreground/60" />
              </div>
              <span className="text-xs font-medium text-sidebar-foreground">
                All Clients
              </span>
            </button>
            <div className="h-px bg-sidebar-border" />
            {mockClients.map((client) => (
              <button
                key={client.id}
                onClick={() => {
                  setSelectedClientId(client.id);
                  setOpen(false);
                }}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-sidebar-accent transition-colors",
                  selectedClientId === client.id && "bg-sidebar-accent"
                )}
              >
                <div
                  className={cn(
                    "h-7 w-7 rounded-md flex items-center justify-center text-white text-[10px] font-bold shrink-0",
                    client.color
                  )}
                >
                  {client.initials}
                </div>
                <span className="text-xs font-medium text-sidebar-foreground">
                  {client.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SidebarContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isContentActive = pathname.startsWith("/content");
  const [contentOpen, setContentOpen] = useState(true);

  // Keep the section open whenever the user is on a content route
  React.useEffect(() => {
    if (isContentActive) setContentOpen(true);
  }, [isContentActive]);

  const renderNavItem = (item: {
    href: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }) => {
    const isActive =
      pathname === item.href || pathname.startsWith(item.href + "/");
    return (
      <Link
        key={item.href}
        href={item.href}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150",
          isActive
            ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm shadow-sidebar-primary/25"
            : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
      >
        <item.icon className="h-[18px] w-[18px]" />
        {item.label}
        {isActive && <ChevronRight className="h-4 w-4 ml-auto opacity-60" />}
      </Link>
    );
  };

  const renderSubNavItem = (item: {
    href: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }) => {
    const isActive =
      pathname === item.href || pathname.startsWith(item.href + "/");
    return (
      <Link
        key={item.href}
        href={item.href}
        className={cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition-all duration-150",
          isActive
            ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm shadow-sidebar-primary/25"
            : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
      >
        <item.icon className="h-4 w-4" />
        {item.label}
      </Link>
    );
  };

  return (
    <div className="flex h-screen">
      <aside className="w-[260px] bg-sidebar text-sidebar-foreground flex flex-col border-r border-sidebar-border">
        {/* Brand */}
        <div className="px-5 py-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-sidebar-primary flex items-center justify-center shadow-md shadow-sidebar-primary/30">
              <Zap className="h-5 w-5 text-sidebar-primary-foreground" />
            </div>
            <div>
              <h1 className="text-[15px] font-bold tracking-tight text-sidebar-foreground">
                Social Standards
              </h1>
              <p className="text-[11px] text-sidebar-foreground/50 font-medium">
                Content Management
              </p>
            </div>
          </div>
        </div>

        {/* Client selector */}
        <ClientSelector />

        <Separator className="bg-sidebar-border mx-4" />

        {/* Main nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/40 mb-2">
            Workspace
          </p>

          {renderNavItem({
            href: "/dashboard",
            label: "Dashboard",
            icon: LayoutDashboard,
          })}

          <div
            className={cn(
              "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150",
              isContentActive
                ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm shadow-sidebar-primary/25"
                : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            )}
          >
            <Link
              href="/content"
              className="flex items-center gap-3 flex-1"
            >
              <Layers className="h-[18px] w-[18px]" />
              Content
            </Link>
            <button
              onClick={() => setContentOpen(!contentOpen)}
              className="p-0.5 rounded hover:bg-sidebar-accent/50"
              aria-label="Toggle content menu"
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform duration-200 opacity-60",
                  !contentOpen && "-rotate-90"
                )}
              />
            </button>
          </div>

          {contentOpen && (
            <div className="ml-4 pl-3 border-l border-sidebar-border space-y-0.5 py-1">
              {contentSubNav.map(renderSubNavItem)}
            </div>
          )}

          {renderNavItem({
            href: "/approvals",
            label: "Approvals",
            icon: ClipboardCheck,
          })}

          {renderNavItem({
            href: "/analytics",
            label: "Analytics",
            icon: BarChart3,
          })}
        </nav>

        {/* Bottom nav */}
        <div className="px-3 pb-2 space-y-1">
          <Separator className="bg-sidebar-border mb-3 mx-1" />
          {bottomNav.map(renderNavItem)}
        </div>

        {/* User */}
        <div className="px-3 pb-4 pt-2">
          <div className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-sidebar-accent transition-colors cursor-pointer">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-sidebar-primary/20 text-sidebar-primary text-xs font-semibold">
                SS
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-foreground truncate">
                My Workspace
              </p>
              <p className="text-[11px] text-sidebar-foreground/50 truncate">
                Free Plan
              </p>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto bg-background">{children}</main>
    </div>
  );
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <ClientProvider>
      <SidebarContent>{children}</SidebarContent>
    </ClientProvider>
  );
}
